import type { Lang } from "../i18n";

// Curated, hand-picked subset of real council-decision records.
// June-led hybrid: the 3 lead cards are the strongest June-2026 decisions
// (verified via the NeoSearch connector's full decision text); the remaining
// 3 are retained April-2026 flagships that keep the carousel geographically
// and sectorally broad (Gesundheit / Mobilität / Kultur) while June's
// budget-confirmed pipeline skewed to education.
//
// Every record is publicly available in the originating Ratsinformationssystem.
//
// Inclusion rules:
//   - real budget only (no "k.A."), confirmed against the decision text
//   - decision date verified (the connector's date field can be a publication
//     date, not the Beschluss date — always confirm before trusting)
//   - status: beschlossen | geplant (no in_prüfung)
//   - source_url = the originating council session, reachable from a browser
//   - mix of regions and sectors so the carousel feels alive
//
// To refresh: query the NeoSearch connector (search_records with a June-style
// date_from/date_to + sort=date_desc), then get_record(include_full_decision)
// each finalist to confirm budget + Beschluss date before pasting here.
// Every DE field with an -En sibling needs its translation refreshed too.

export type VorsprungRecord = {
  id: string;
  title: string;
  city: string;
  district: string | null;
  sector: string;
  budget: string;
  decisionDate: string; // YYYY-MM-DD — date of council decision (source field)
  decisionDateLabel: string; // localized display, e.g. "23.04.2026"
  vergabe: string | null; // "Mai 2027" / null if not stated
  status: "beschlossen" | "geplant";
  sourceLabel: string; // "Tübingen · Ratsinformationssystem"
  sourceUrl: string;
  recordId?: number;
  decision?: string;
  decisionEn?: string;
  stage?: string; // decision type shown as a chip, e.g. "Ratsbeschluss"
  stageEn?: string;
  reason?: string; // "Warum relevant" one-liner shown for the active ticker row
  reasonEn?: string;
  // Responsible administrative unit as named in the source. Not a person —
  // the dossier card labels this "Fachbereich".
  department?: string;
  departmentEn?: string;
  // Illustrative decision-maker roles for the dossier card. Roles reflect the
  // responsible bodies in the record; names are NEVER real (rendered blurred).
  deciders?: { role: string }[];
};

// Monitor carousel — 6 rows, mix of city + sector + scale.
// First 3 (June headliners) also feed the "Aktuelle Beschlüsse" week cards.
export const MONITOR_RECORDS: VorsprungRecord[] = [
  {
    id: "bergheim-grundschule",
    title: "Neubau dreizügige Grundschule Albrecht-Dürer-Allee",
    city: "Bergheim",
    district: null,
    sector: "Bildung",
    budget: "24,5 Mio. €",
    decisionDate: "2026-06-30",
    decisionDateLabel: "30.06.2026",
    vergabe: null,
    status: "beschlossen",
    sourceLabel: "Bergheim · Ratsinformationssystem",
    sourceUrl:
      "https://ratsinfo.bergheim.de/tops/?__=UGhVM0hpd2NXNFdFcExjZda58Do4f2YsdtHSZIuPFE4",
    decision:
      "Ausschuss beschließt einstimmig den Neubau einer dreizügigen Grundschule als Offene Ganztagsschule. Gesamtkosten rund 24,5 Mio. €, Fertigstellung zum Schuljahr 2030/31.",
    decisionEn:
      "Committee unanimously approves the construction of a new three-form all-day primary school. Total cost around €24.5M, completion targeted for the 2030/31 school year.",
    stage: "Ausschussbeschluss",
    stageEn: "Committee decision",
    reason:
      "Neubau-Beschluss mit bestätigtem Budget — passt auf Monitor „Schulbau NRW über 10 Mio. €“.",
    reasonEn:
      'New-build decision with a confirmed budget — matches the monitor "school construction NRW above €10M".',
    department: "Amt für Schulverwaltung",
    departmentEn: "School Administration Department",
    deciders: [
      { role: "Leitung Amt für Schulverwaltung · Stadt Bergheim" },
      { role: "Vorsitz Ausschuss für Bildung, Sport und Kultur" },
    ],
  },
  {
    id: "moenchengladbach-ogs-rheydt",
    title: "Neubau OGS und Sanierung Grundschule Wilhelm-Strauß-Straße",
    city: "Mönchengladbach",
    district: "Rheydt",
    sector: "Bildung",
    budget: "20,7 Mio. €",
    decisionDate: "2026-06-25",
    decisionDateLabel: "25.06.2026",
    vergabe: null,
    status: "beschlossen",
    sourceLabel: "Mönchengladbach · Ratsinformationssystem",
    sourceUrl:
      "https://ris-moenchengladbach.itk-rheinland.de/sessionnetmglbi/si0057.asp?__ksinr=8507",
    decision:
      "Beschluss zum Neubau der Offenen Ganztagsschule und zur energetischen Sanierung der Gemeinschaftsgrundschule Wilhelm-Strauß-Straße, brutto 20,7 Mio. €. Umsetzung ab Haushaltsplan 2027 ff.",
    decisionEn:
      "Decision to build a new all-day school and carry out an energy retrofit of the Wilhelm-Strauß-Straße primary school, gross €20.7M. Implementation from the 2027 budget onward.",
    stage: "Ausschussbeschluss",
    stageEn: "Committee decision",
    reason:
      "Neubau plus Sanierung in einem Beschluss — Förderquote 73 %, Umsetzung ab Haushalt 2027.",
    reasonEn:
      "New build plus retrofit in one decision — 73% funding rate, implementation from the 2027 budget.",
  },
  {
    id: "schwerin-fridericianum",
    title: "Sanierung Gymnasium Fridericianum",
    city: "Schwerin",
    district: null,
    sector: "Bildung",
    budget: "13,8 Mio. €",
    decisionDate: "2026-06-22",
    decisionDateLabel: "22.06.2026",
    vergabe: null,
    status: "beschlossen",
    sourceLabel: "Schwerin · Ratsinformationssystem",
    sourceUrl: "https://bis.schwerin.de/si0057.asp?__ksinr=11809",
    decision:
      "Stadtvertretung beschließt im LuKIFG-Investitionspaket 13,8 Mio. € für die Sanierung des Gymnasiums Fridericianum. Planung ab 2027, Baubeginn ab 2030.",
    decisionEn:
      "City council approves €13.8M within the LuKIFG investment package for the renovation of the Fridericianum grammar school. Planning from 2027, construction start from 2030.",
    stage: "Ratsbeschluss",
    stageEn: "Council decision",
    reason:
      "Teil eines 32,4-Mio.-€-Investitionspakets — Planung ab 2027, früher Kontaktzeitpunkt.",
    reasonEn:
      "Part of a €32.4M investment package — planning starts 2027, an early point to make contact.",
  },
  {
    id: "magdeburg-klinikum",
    title: "Universitätsklinikum Magdeburg, neues Zentralkrankenhaus",
    city: "Magdeburg",
    district: null,
    sector: "Gesundheit",
    budget: "> 1 Mrd. €",
    decisionDate: "2026-04-14",
    decisionDateLabel: "14.04.2026",
    vergabe: null,
    status: "geplant",
    sourceLabel: "Magdeburg · Ratsinformationssystem",
    sourceUrl: "https://ratsinfo.magdeburg.de/si0057.asp?__ksinr=124728",
    recordId: 16288,
    decision:
      "Planungsbeschluss für ein neues Zentralkrankenhaus mit einem Volumen von über 1 Mrd. €. Großvorhaben im frühen Planungsstadium.",
    decisionEn:
      "Planning resolution for a new central hospital with a volume of more than €1B. A major project at an early planning stage.",
    stage: "Planungsbeschluss",
    stageEn: "Planning resolution",
    reason:
      "Großvorhaben im frühesten Stadium — Jahre vor der ersten Ausschreibung sichtbar.",
    reasonEn:
      "A major project at its earliest stage — visible years before the first tender.",
    department: "Bau- und Liegenschaftsmanagement",
    departmentEn: "Construction and Property Management",
    deciders: [
      { role: "Kaufmännischer Vorstand · Universitätsklinikum Magdeburg" },
      { role: "Leitung Bau- und Liegenschaftsmanagement" },
    ],
  },
  {
    id: "tuebingen-parkhaus",
    title: "Neubau Parkhaus Tübingen",
    city: "Tübingen",
    district: null,
    sector: "Mobilität",
    budget: "10,8 Mio. €",
    decisionDate: "2026-04-23",
    decisionDateLabel: "23.04.2026",
    vergabe: "Mai 2027",
    status: "beschlossen",
    sourceLabel: "Tübingen · Gemeinderat",
    sourceUrl: "https://www.tuebingen.de/gemeinderat/si0057.php?__ksinr=9683",
    recordId: 16854,
    decision:
      "Gemeinderat beschließt den Neubau eines Parkhauses mit einem Budget von max. 10,8 Mio. €. Realisierung geplant für Mai 2027.",
    decisionEn:
      "City council approves the construction of a new car park with a budget of up to €10.8M. Completion planned for May 2027.",
    stage: "Ratsbeschluss",
    stageEn: "Council decision",
    reason:
      "Budget und Realisierungszeitraum beschlossen — Vergabe für Mai 2027 angekündigt.",
    reasonEn:
      "Budget and timeline approved — the tender is expected for May 2027.",
    department: "Fachbereich Tiefbau",
    departmentEn: "Civil Engineering Department",
    deciders: [
      { role: "Leitung Fachbereich Tiefbau · Stadt Tübingen" },
      { role: "Vorsitz Ausschuss für Umwelt, Klima und Verkehr" },
    ],
  },
  {
    id: "leipzig-kohlrabi",
    title: "Sanierung Kohlrabizirkus Sport- und Kulturzentrum",
    city: "Leipzig",
    district: "Mitte",
    sector: "Sport & Kultur",
    budget: "30,0 Mio. €",
    decisionDate: "2026-04-23",
    decisionDateLabel: "23.04.2026",
    vergabe: null,
    status: "geplant",
    sourceLabel: "Leipzig · Ratsinformationssystem",
    sourceUrl:
      "https://ratsinformation.leipzig.de/allris_leipzig_public/to010?SILFDNR=2004779&refresh=false",
    recordId: 16181,
    decision:
      "Beschluss zur Sanierung des Kohlrabizirkus als Sport- und Kulturzentrum, ca. 30 Mio. €. Denkmalgeschütztes Bestandsgebäude.",
    decisionEn:
      "Decision to renovate the Kohlrabizirkus as a sports and culture centre, approx. €30M. A listed heritage building.",
    stage: "Ratsbeschluss",
    stageEn: "Council decision",
    reason:
      "Denkmalgeschützter Bestand, 30 Mio. € — Fachplanung wird früh gebraucht.",
    reasonEn:
      "A listed heritage building, €30M — specialist planning is needed early.",
    department: "Amt für Sport",
    departmentEn: "Sports Department",
    deciders: [
      { role: "Leitung Amt für Sport · Stadt Leipzig" },
      { role: "Vorsitz Ausschuss für Kultur" },
    ],
  },
  // Ticker-only rows (beyond the first 3, they never appear as cards):
  // the remaining verified decisions from Schwerin's 22.06.2026 LuKIFG
  // investment package — more stream variety without diluting the cards.
  {
    id: "schwerin-ese-lankow",
    title: "Neubau Schulgebäude für esE-Klassen, Standort Lankow",
    city: "Schwerin",
    district: null,
    sector: "Bildung",
    budget: "11,0 Mio. €",
    decisionDate: "2026-06-22",
    decisionDateLabel: "22.06.2026",
    vergabe: null,
    status: "beschlossen",
    sourceLabel: "Schwerin · Ratsinformationssystem",
    sourceUrl: "https://bis.schwerin.de/si0057.asp?__ksinr=11809",
    decision:
      "Stadtvertretung beschließt 11,0 Mio. € für den Neubau eines Schulgebäudes für esE-Klassen am Standort Lankow. Umsetzung 2026–2028.",
    decisionEn:
      "City council approves €11.0M for a new school building for special-needs classes at the Lankow site. Implementation 2026–2028.",
    stage: "Ratsbeschluss",
    stageEn: "Council decision",
    reason:
      "Neubau mit kurzem Zeithorizont 2026–2028 — Planungsleistungen werden sofort gebraucht.",
    reasonEn:
      "New build on a short 2026–2028 timeline — planning services are needed immediately.",
  },
  {
    id: "schwerin-sport-weststadt",
    title: "Sanierung Schulsportanlage Weststadt mit Funktionsgebäude",
    city: "Schwerin",
    district: null,
    sector: "Sport & Kultur",
    budget: "6,0 Mio. €",
    decisionDate: "2026-06-22",
    decisionDateLabel: "22.06.2026",
    vergabe: null,
    status: "beschlossen",
    sourceLabel: "Schwerin · Ratsinformationssystem",
    sourceUrl: "https://bis.schwerin.de/si0057.asp?__ksinr=11809",
    decision:
      "Stadtvertretung beschließt 6,0 Mio. € für die Sanierung der Schulsportanlage Weststadt inklusive neuem Funktionsgebäude.",
    decisionEn:
      "City council approves €6.0M to renovate the Weststadt school sports facility, including a new amenities building.",
    stage: "Ratsbeschluss",
    stageEn: "Council decision",
    reason:
      "Sanierung plus Neubau im selben Beschluss — zwei Gewerke, ein Ansprechpartner.",
    reasonEn:
      "Renovation plus new build in the same decision — two trades, one contact.",
  },
  {
    id: "schwerin-muess-eingang",
    title: "Freilichtmuseum Mueß, Neubau Eingangsgebäude",
    city: "Schwerin",
    district: null,
    sector: "Sport & Kultur",
    budget: "4,0 Mio. €",
    decisionDate: "2026-06-22",
    decisionDateLabel: "22.06.2026",
    vergabe: null,
    status: "beschlossen",
    sourceLabel: "Schwerin · Ratsinformationssystem",
    sourceUrl: "https://bis.schwerin.de/si0057.asp?__ksinr=11809",
    decision:
      "Stadtvertretung beschließt 4,0 Mio. € für den Neubau des Eingangsgebäudes am Freilichtmuseum Mueß. Umsetzung 2028/29.",
    decisionEn:
      "City council approves €4.0M for a new entrance building at the Mueß open-air museum. Implementation 2028/29.",
    stage: "Ratsbeschluss",
    stageEn: "Council decision",
    reason:
      "Kulturbau im frühen Stadium — Wettbewerb und Vergabe folgen erst ab 2027.",
    reasonEn:
      "A cultural building at an early stage — competition and tender only follow from 2027.",
  },
];

// Records shown as full rotating dossiers in the "Aktuelle Beschlüsse"
// carousel — each has `deciders` (the People teaser). A deliberate spread of
// sector + geography + scale so the rotation reads as breadth, not one project.
export const DOSSIER_RECORDS: VorsprungRecord[] = [
  MONITOR_RECORDS[0], // Bergheim · Bildung · 24,5 Mio. €
  MONITOR_RECORDS[3], // Magdeburg · Gesundheit · > 1 Mrd. €
  MONITOR_RECORDS[4], // Tübingen · Mobilität · 10,8 Mio. €
  MONITOR_RECORDS[5], // Leipzig · Sport & Kultur · 30,0 Mio. €
];

// Single "last week's headline" record for the digest exemplar card.
// Picked for max signal-to-noise: big budget, clear scope, top-of-pipeline.
export const DIGEST_RECORD: VorsprungRecord = MONITOR_RECORDS[0]; // Bergheim Grundschule

// Locale-aware field pickers — fall back to the German source if a
// translation is missing so a copy gap never renders blank.
export const pickDecision = (r: VorsprungRecord, lang: Lang) =>
  (lang === "en" ? r.decisionEn : undefined) ?? r.decision;

export const pickStage = (r: VorsprungRecord, lang: Lang) =>
  (lang === "en" ? r.stageEn : undefined) ?? r.stage;

export const pickReason = (r: VorsprungRecord, lang: Lang) =>
  (lang === "en" ? r.reasonEn : undefined) ?? r.reason;

export const pickDepartment = (r: VorsprungRecord, lang: Lang) =>
  (lang === "en" ? r.departmentEn : undefined) ?? r.department;
